import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, FileText, User, Camera, ChevronRight, Check, Loader2,
  Sparkles, ArrowRight, X, Briefcase, GraduationCap, MapPin,
  BarChart3, Star, TrendingUp, Zap, Target, AlertCircle, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'resume', label: 'Upload Resume', icon: FileText },
  { id: 'ats', label: 'ATS Score', icon: BarChart3 },
  { id: 'assessment', label: 'Career Review', icon: Award },
  { id: 'photo', label: 'Profile Photo', icon: Camera },
  { id: 'review', label: 'Review & Start', icon: User },
];

export default function OnboardingPage({ onComplete }) {
  const [step, setStep] = useState(0);
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsedProfile, setParsedProfile] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [atsScore, setAtsScore] = useState(null);
  const [atsLoading, setAtsLoading] = useState(false);
  const [careerAssessment, setCareerAssessment] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [form, setForm] = useState({
    title: '', bio: '', location: '', current_company: '', experience_years: 0,
  });
  const [completing, setCompleting] = useState(false);
  const resumeRef = useRef(null);
  const picRef = useRef(null);
  const navigate = useNavigate();

  const handleResumeUpload = useCallback(async (file) => {
    if (!file) return;
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PDF or DOCX file');
      return;
    }
    setResumeFile(file);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/onboarding/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setParsedProfile(data.parsed_profile || {});
      setForm(prev => ({
        ...prev,
        title: data.parsed_profile?.title || prev.title,
        bio: data.parsed_profile?.bio || prev.bio,
        location: data.parsed_profile?.location || prev.location,
        current_company: data.parsed_profile?.current_company || prev.current_company,
        experience_years: data.parsed_profile?.experience_years || prev.experience_years,
      }));
      toast.success('Resume parsed successfully!');
      setStep(1); // Go to ATS Score step
      // Trigger ATS scoring in background
      setAtsLoading(true);
      api.post('/onboarding/resume-score').then(r => setAtsScore(r.data)).catch(() => {}).finally(() => setAtsLoading(false));
    } catch (err) {
      toast.error('Failed to upload resume. Please try again.');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleProfilePicUpload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    setProfilePic(file);
    setProfilePicPreview(URL.createObjectURL(file));
    setUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/onboarding/profile-picture`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      toast.success('Profile picture uploaded!');
    } catch (err) {
      toast.error('Failed to upload picture');
    } finally {
      setUploadingPic(false);
    }
  }, []);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      // Save profile info
      await api.put('/profile', {
        title: form.title,
        bio: form.bio,
        location: form.location,
        current_company: form.current_company,
        experience_years: parseInt(form.experience_years) || 0,
      });
      // Mark onboarding complete
      await api.post('/onboarding/complete');
      toast.success('Profile setup complete! Welcome to Kairera.');
      if (onComplete) onComplete();
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setCompleting(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0D1B3E' }}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4 max-w-2xl mx-auto w-full">
        <div className="flex items-center gap-3 mb-6">
          <img src="/kairera-white.png" alt="Kairera" style={{ height: 36 }} />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: '#F7FAFC' }}>
          Let's set up your profile
        </h1>
        <p className="text-sm mt-1" style={{ color: '#9DB5DC' }}>
          Upload your resume and we'll auto-fill your career profile
        </p>
      </div>

      {/* Progress */}
      <div className="px-6 max-w-2xl mx-auto w-full">
        <div className="flex items-center gap-2 mb-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <div key={s.id} className="flex items-center gap-2 flex-1">
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                    style={{
                      background: isDone ? '#0D9488' : isActive ? 'rgba(13,148,136,0.15)' : 'rgba(255,255,255,0.05)',
                      color: isDone ? '#fff' : isActive ? '#0D9488' : '#4D72BE',
                      border: isActive ? '1.5px solid #0D9488' : '1px solid transparent',
                    }}>
                    {isDone ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                  </div>
                  <span className="text-[10px] font-semibold hidden sm:inline"
                    style={{ color: isActive ? '#F7FAFC' : '#7595CC' }}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px" style={{ background: isDone ? '#0D9488' : 'rgba(255,255,255,0.08)' }} />
                )}
              </div>
            );
          })}
        </div>
        <Progress value={progress} className="h-1 mt-2" />
      </div>

      {/* Step Content */}
      <div className="flex-1 px-6 py-8 max-w-2xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {/* Step 1: Resume Upload */}
          {step === 0 && (
            <motion.div key="resume" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="rounded-2xl p-8 text-center"
                style={{ background: 'rgba(13,30,58,0.7)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <input ref={resumeRef} type="file" accept=".pdf,.docx" className="hidden"
                  onChange={e => handleResumeUpload(e.target.files[0])} data-testid="resume-file-input" />

                {!resumeFile && !uploading && (
                  <>
                    <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
                      style={{ background: 'rgba(13,148,136,0.1)' }}>
                      <Upload className="w-8 h-8" style={{ color: '#0D9488' }} />
                    </div>
                    <h2 className="text-lg font-bold mb-2" style={{ color: '#F7FAFC' }}>Upload Your Resume</h2>
                    <p className="text-sm mb-6" style={{ color: '#9DB5DC' }}>
                      We'll extract your skills, experience, and education to jumpstart your career profile.
                    </p>
                    <Button onClick={() => resumeRef.current?.click()} data-testid="upload-resume-btn"
                      className="h-12 px-8 text-sm font-semibold rounded-xl gap-2"
                      style={{ background: '#0D9488', color: '#fff' }}>
                      <FileText className="w-4 h-4" /> Choose PDF or DOCX
                    </Button>
                    <p className="text-[10px] mt-4" style={{ color: '#4D72BE' }}>Max 10MB. Your file is encrypted and stored securely.</p>
                  </>
                )}

                {uploading && (
                  <div className="py-8">
                    <Loader2 className="w-12 h-12 mx-auto animate-spin mb-4" style={{ color: '#0D9488' }} />
                    <h2 className="text-lg font-bold mb-1" style={{ color: '#F7FAFC' }}>Analyzing your resume...</h2>
                    <p className="text-sm" style={{ color: '#9DB5DC' }}>
                      Extracting your skills, experience, and education
                    </p>
                  </div>
                )}

                {resumeFile && !uploading && parsedProfile && (
                  <div className="text-left space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(13,148,136,0.1)', border: '1px solid rgba(13,148,136,0.2)' }}>
                      <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#0D9488' }} />
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#F7FAFC' }}>{resumeFile.name}</p>
                        <p className="text-[10px]" style={{ color: '#9DB5DC' }}>
                          {parsedProfile.skills?.length || 0} skills extracted | {parsedProfile.experience?.length || 0} roles found
                        </p>
                      </div>
                    </div>
                    <Button onClick={() => setStep(1)} data-testid="continue-to-photo-btn"
                      className="w-full h-11 font-semibold rounded-lg gap-2"
                      style={{ background: '#0D9488', color: '#fff' }}>
                      Continue <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Skip option */}
              <button onClick={() => setStep(3)} className="mt-4 text-xs font-medium block mx-auto transition-colors"
                style={{ color: '#7595CC' }} data-testid="skip-resume-btn">
                Skip for now. I'll add my resume later
              </button>
            </motion.div>
          )}

          {/* Step 2: ATS Score */}
          {step === 1 && (
            <motion.div key="ats" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="rounded-2xl p-6" style={{ background: 'rgba(13,30,58,0.7)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5" style={{ color: '#0D9488' }} />
                  <h2 className="text-lg font-bold" style={{ color: '#F7FAFC' }}>ATS Compatibility Score</h2>
                </div>

                {atsLoading ? (
                  <div className="py-10 text-center">
                    <Loader2 className="w-10 h-10 mx-auto animate-spin mb-3" style={{ color: '#0D9488' }} />
                    <p className="text-sm" style={{ color: '#9DB5DC' }}>Analyzing your resume for ATS compatibility...</p>
                  </div>
                ) : atsScore ? (
                  <div className="space-y-4">
                    {/* Overall Score */}
                    <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(13,148,136,0.08)' }}>
                      <p className="text-4xl font-black" style={{ color: atsScore.overall_score >= 70 ? '#0D9488' : atsScore.overall_score >= 50 ? '#F5841F' : '#DC2626' }}>
                        {atsScore.overall_score}
                      </p>
                      <p className="text-xs font-semibold" style={{ color: '#9DB5DC' }}>out of 100</p>
                      <p className="text-[10px] mt-1" style={{ color: '#7595CC' }}>
                        {atsScore.ats_friendly ? 'ATS Friendly' : 'Needs Improvement'} | Keyword Density: {atsScore.keyword_density}
                      </p>
                    </div>

                    {/* Section Scores */}
                    {atsScore.sections?.map((s, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] font-semibold" style={{ color: '#C4D3EE' }}>{s.name}</span>
                            <span className="text-[10px] font-bold" style={{ color: s.score >= 70 ? '#0D9488' : s.score >= 50 ? '#F5841F' : '#DC2626' }}>
                              {s.score}/100
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <div className="h-full rounded-full transition-all" style={{
                              width: `${s.score}%`,
                              background: s.score >= 70 ? '#0D9488' : s.score >= 50 ? '#F5841F' : '#DC2626',
                            }} />
                          </div>
                          <p className="text-[9px] mt-0.5" style={{ color: '#7595CC' }}>{s.feedback}</p>
                        </div>
                      </div>
                    ))}

                    {/* Top Improvements */}
                    {atsScore.top_improvements?.length > 0 && (
                      <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(245,132,31,0.06)', border: '1px solid rgba(245,132,31,0.12)' }}>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#F5841F' }}>Top Improvements</p>
                        {atsScore.top_improvements.map((tip, i) => (
                          <div key={i} className="flex items-start gap-2 mb-1.5">
                            <TrendingUp className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#F5841F' }} />
                            <p className="text-[10px]" style={{ color: '#C4D3EE' }}>{tip}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    <Button onClick={() => { setStep(2); setAssessmentLoading(true); api.post('/onboarding/career-assessment').then(r => setCareerAssessment(r.data)).catch(() => {}).finally(() => setAssessmentLoading(false)); }}
                      data-testid="continue-to-assessment-btn"
                      className="w-full h-11 font-semibold rounded-lg gap-2"
                      style={{ background: '#0D9488', color: '#fff' }}>
                      View Career Assessment <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-sm" style={{ color: '#9DB5DC' }}>Score unavailable</p>
                    <Button onClick={() => setStep(2)} className="mt-3 h-9 px-5 text-xs font-semibold rounded-lg"
                      style={{ background: '#0D9488', color: '#fff' }}>Continue</Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Panel Career Assessment */}
          {step === 2 && (
            <motion.div key="assessment" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="rounded-2xl p-6" style={{ background: 'rgba(13,30,58,0.7)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5" style={{ color: '#0D9488' }} />
                  <h2 className="text-lg font-bold" style={{ color: '#F7FAFC' }}>Expert Panel Career Review</h2>
                </div>
                <p className="text-xs mb-5" style={{ color: '#9DB5DC' }}>
                  Our panel of career experts has reviewed your journey so far.
                </p>

                {assessmentLoading ? (
                  <div className="py-10 text-center">
                    <Loader2 className="w-10 h-10 mx-auto animate-spin mb-3" style={{ color: '#0D9488' }} />
                    <p className="text-sm" style={{ color: '#9DB5DC' }}>Expert panel is reviewing your career journey...</p>
                  </div>
                ) : careerAssessment && !careerAssessment.error ? (
                  <div className="space-y-4">
                    {/* Score & Stage */}
                    <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'rgba(13,148,136,0.08)' }}>
                      <div className="text-center">
                        <p className="text-3xl font-black" style={{ color: '#0D9488' }}>{careerAssessment.career_score}/10</p>
                        <p className="text-[10px] font-semibold" style={{ color: '#9DB5DC' }}>Career Score</p>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-bold" style={{ color: '#C4D3EE' }}>{careerAssessment.career_stage}</p>
                        <p className="text-[11px] mt-1 leading-relaxed" style={{ color: '#9DB5DC' }}>
                          {careerAssessment.panel_verdict}
                        </p>
                      </div>
                    </div>

                    {/* Strengths */}
                    {careerAssessment.what_they_did_well?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#16A34A' }}>What You Did Well</p>
                        {careerAssessment.what_they_did_well.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 mb-1.5">
                            <Star className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#16A34A' }} />
                            <p className="text-[11px]" style={{ color: '#C4D3EE' }}>{s}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Skills Gap */}
                    {careerAssessment.skills_gap?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#F5841F' }}>Skills to Develop</p>
                        <div className="flex flex-wrap gap-1.5">
                          {careerAssessment.skills_gap.map((s, i) => (
                            <span key={i} className="text-[10px] px-2 py-1 rounded-full font-medium"
                              style={{ background: 'rgba(245,132,31,0.1)', color: '#F5841F', border: '1px solid rgba(245,132,31,0.2)' }}>
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Next Steps */}
                    {careerAssessment.next_steps?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#0D9488' }}>Recommended Next Steps</p>
                        {careerAssessment.next_steps.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 mb-2 p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                            <Target className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#0D9488' }} />
                            <div>
                              <p className="text-[11px] font-semibold" style={{ color: '#C4D3EE' }}>{s.action}</p>
                              <p className="text-[9px]" style={{ color: '#7595CC' }}>
                                Impact: {s.impact} | Timeline: {s.timeline}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Motivation */}
                    {careerAssessment.motivation && (
                      <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(13,148,136,0.06)', border: '1px solid rgba(13,148,136,0.12)' }}>
                        <Sparkles className="w-5 h-5 mx-auto mb-2" style={{ color: '#0D9488' }} />
                        <p className="text-xs leading-relaxed italic" style={{ color: '#C4D3EE' }}>
                          "{careerAssessment.motivation}"
                        </p>
                      </div>
                    )}

                    <Button onClick={() => setStep(3)} data-testid="continue-to-photo-btn"
                      className="w-full h-11 font-semibold rounded-lg gap-2"
                      style={{ background: '#0D9488', color: '#fff' }}>
                      Continue <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-sm mb-3" style={{ color: '#9DB5DC' }}>Assessment unavailable</p>
                    <Button onClick={() => setStep(3)} className="h-9 px-5 text-xs font-semibold rounded-lg"
                      style={{ background: '#0D9488', color: '#fff' }}>Continue</Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 4: Profile Picture */}
          {step === 3 && (
            <motion.div key="photo" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="rounded-2xl p-8 text-center"
                style={{ background: 'rgba(13,30,58,0.7)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <input ref={picRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={e => handleProfilePicUpload(e.target.files[0])} data-testid="profile-pic-input" />

                {!profilePicPreview ? (
                  <>
                    <div className="w-24 h-24 rounded-full mx-auto mb-5 flex items-center justify-center"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '2px dashed rgba(255,255,255,0.15)' }}>
                      <Camera className="w-8 h-8" style={{ color: '#7595CC' }} />
                    </div>
                    <h2 className="text-lg font-bold mb-2" style={{ color: '#F7FAFC' }}>Add a Profile Photo</h2>
                    <p className="text-sm mb-6" style={{ color: '#9DB5DC' }}>
                      A professional photo helps build trust and makes your profile stand out.
                    </p>
                    <Button onClick={() => picRef.current?.click()} data-testid="upload-photo-btn"
                      className="h-11 px-6 text-sm font-semibold rounded-xl gap-2"
                      style={{ background: '#0D9488', color: '#fff' }}>
                      <Camera className="w-4 h-4" /> Upload Photo
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="relative w-28 h-28 mx-auto mb-5">
                      <img src={profilePicPreview} alt="Profile"
                        className="w-full h-full rounded-full object-cover" style={{ border: '3px solid #0D9488' }} />
                      {uploadingPic && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                          <Loader2 className="w-6 h-6 animate-spin text-white" />
                        </div>
                      )}
                      <button onClick={() => { setProfilePic(null); setProfilePicPreview(null); }}
                        className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: '#DC2626' }}>
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                    <p className="text-sm font-semibold mb-4" style={{ color: '#F7FAFC' }}>Looking great!</p>
                    <Button onClick={() => setStep(4)} data-testid="continue-to-review-btn"
                      className="w-full h-11 font-semibold rounded-lg gap-2"
                      style={{ background: '#0D9488', color: '#fff' }}>
                      Continue <ArrowRight className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>

              <button onClick={() => setStep(4)} className="mt-4 text-xs font-medium block mx-auto transition-colors"
                style={{ color: '#7595CC' }} data-testid="skip-photo-btn">
                Skip for now
              </button>
            </motion.div>
          )}

          {/* Step 5: Review & Confirm */}
          {step === 4 && (
            <motion.div key="review" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div className="rounded-2xl p-6"
                style={{ background: 'rgba(13,30,58,0.7)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2 mb-5">
                  <Sparkles className="w-4 h-4" style={{ color: '#0D9488' }} />
                  <h2 className="text-lg font-bold" style={{ color: '#F7FAFC' }}>Review Your Profile</h2>
                </div>
                <p className="text-sm mb-6" style={{ color: '#9DB5DC' }}>
                  Confirm or edit the details extracted from your resume. You can always update these later.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: '#9DB5DC' }}>Job Title</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9DB5DC' }} />
                      <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. Senior Software Engineer" className="k-input-dark pl-10 h-11" data-testid="onboarding-title" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: '#9DB5DC' }}>Current Company</label>
                    <div className="relative">
                      <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9DB5DC' }} />
                      <Input value={form.current_company} onChange={e => setForm(p => ({ ...p, current_company: e.target.value }))}
                        placeholder="e.g. Google" className="k-input-dark pl-10 h-11" data-testid="onboarding-company" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: '#9DB5DC' }}>Location</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9DB5DC' }} />
                      <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                        placeholder="e.g. San Francisco, CA" className="k-input-dark pl-10 h-11" data-testid="onboarding-location" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: '#9DB5DC' }}>Years of Experience</label>
                    <Input type="number" value={form.experience_years} onChange={e => setForm(p => ({ ...p, experience_years: e.target.value }))}
                      placeholder="0" className="k-input-dark h-11 w-24" data-testid="onboarding-years" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: '#9DB5DC' }}>Professional Summary</label>
                    <Textarea value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                      placeholder="A brief summary about your career background..."
                      rows={3} className="k-input-dark" data-testid="onboarding-bio" />
                  </div>
                </div>

                {/* Skills preview */}
                {parsedProfile?.skills?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#9DB5DC' }}>
                      Extracted Skills ({parsedProfile.skills.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {parsedProfile.skills.slice(0, 15).map((s, i) => (
                        <span key={i} className="text-[10px] px-2 py-1 rounded-full font-medium"
                          style={{ background: 'rgba(13,148,136,0.1)', color: '#0D9488', border: '1px solid rgba(13,148,136,0.2)' }}>
                          {s}
                        </span>
                      ))}
                      {parsedProfile.skills.length > 15 && (
                        <span className="text-[10px] px-2 py-1 font-medium" style={{ color: '#7595CC' }}>
                          +{parsedProfile.skills.length - 15} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <Button onClick={handleComplete} disabled={completing} data-testid="complete-onboarding-btn"
                  className="w-full h-12 mt-6 font-bold uppercase tracking-wider text-sm rounded-lg gap-2"
                  style={{ background: '#0D9488', color: '#fff' }}>
                  {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {completing ? 'Setting up...' : 'Complete Setup & Start'}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
